import { useSyncExternalStore } from 'react';
import { getStore, storeKeys } from 'core/storage/store';

export type ThemeMode = 'light' | 'dark' | 'system';

const DEFAULT_THEME: ThemeMode = 'system';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches;

// Whether the resolved appearance is dark for a given mode.
const isDark = (mode: ThemeMode): boolean =>
  mode === 'dark' || (mode === 'system' && prefersDark());

export const getStoredTheme = (): ThemeMode =>
  (getStore().getItem(storeKeys.theme) as ThemeMode | null) ?? DEFAULT_THEME;

// Toggle the `dark` class on <html> (Tailwind's `darkMode: 'class'`).
export const applyTheme = (mode: ThemeMode): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', isDark(mode));
};

// Apply the persisted theme and keep it in sync with the OS when set to
// `system`. Call once at the app root; returns a cleanup for the listener.
export const applyStoredTheme = (): (() => void) => {
  applyTheme(getStoredTheme());

  if (typeof window === 'undefined') return () => {};
  const mql = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (getStoredTheme() === 'system') applyTheme('system');
  };
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
};

// Everyone using useTheme shares one theme. Changing it from one place (the
// rail's toggle, the Appearance page) tells all the others, so they always
// show the same choice.
const listeners = new Set<() => void>();

const subscribeToTheme = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// The device's light/dark setting, for when the theme is `system`.
const subscribeToDevice = (listener: () => void) => {
  const mql = window.matchMedia(DARK_QUERY);
  mql.addEventListener('change', listener);
  return () => mql.removeEventListener('change', listener);
};

// Saves, applies and announces a new theme.
export const setTheme = (mode: ThemeMode): void => {
  getStore().setItem(storeKeys.theme, mode);
  applyTheme(mode);
  listeners.forEach((listener) => listener());
};

// The theme for the settings UI and the rail's toggle. `resolvedTheme` is
// what's on screen ('light' | 'dark') once `system` is checked against the
// device. The server and the first render use the defaults, so the page
// hydrates without a mismatch, then the saved theme takes over.
export const useTheme = () => {
  const theme = useSyncExternalStore(
    subscribeToTheme,
    getStoredTheme,
    () => DEFAULT_THEME,
  );
  const systemDark = useSyncExternalStore(
    subscribeToDevice,
    prefersDark,
    () => false,
  );

  const resolvedTheme: 'light' | 'dark' =
    theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  return { theme, resolvedTheme, setTheme };
};
